import React, { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAsyncLoad } from "@/hooks/useAsyncLoad";
import { Loading, LoadFailed } from "@/components/LoadState";
import { Crosshair } from "@/components/Crosshair";
import { toast } from "@/lib/toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import VerificationTaxonomyAudit from "@/components/admin/VerificationTaxonomyAudit";
import CommissionImpactReport from "@/components/admin/CommissionImpactReport";
import {
  Settings, Save, Shield, Clock, Flag, Layers, Plus, Trash2, Pencil,
  CheckCircle2, XCircle, Fingerprint, AtSign, Award,
} from "lucide-react";

const FIELDS = [
  { key: "ip_retention_days", label: "IP retention (days)", icon: Fingerprint, note: "Raw IPs are one-way hashed after this window." },
  { key: "activity_retention_days", label: "Activity retention (days)", icon: Clock, note: "Activity rows purged after this window." },
  { key: "escrow_fallback_release_days", label: "Marketplace fallback release (days)", icon: Clock, note: "Auto-release held funds if the buyer goes silent." },
  { key: "payout_completion_days", label: "Payout-completion window (days)", icon: Clock, note: "After a sale, the seller has this long to connect payouts before the buyer is auto-refunded." },
  { key: "block_signal_threshold", label: "Block signal threshold", icon: Flag, note: "Distinct blocks before an account surfaces in moderation." },
  { key: "username_cooldown_days", label: "Username change cooldown (days)", icon: AtSign, note: "0 = users can rename freely (beta). Raise to 30 at launch." },
  { key: "username_reserve_days", label: "Released-handle reserve (days)", icon: AtSign, note: "A released username stays reserved + redirects for this long (impersonation control)." },
];

// Earned-badge thresholds — tuned live once real order/message data exists.
const BADGE_FIELDS = [
  { key: "rating_min_sample", label: "Rating — min rated sales" },
  { key: "top_rated_min_pct", label: "Top Rated — min positive %" },
  { key: "top_rated_min_sample", label: "Top Rated — min rated sales" },
  { key: "fast_shipper_min_sample", label: "Fast Shipper — min shipped orders" },
  { key: "fast_shipper_max_hours", label: "Fast Shipper — max median ship (hrs)" },
  { key: "quick_reply_min_sample", label: "Quick Reply — min answered threads" },
  { key: "quick_reply_max_hours", label: "Quick Reply — max median reply (hrs)" },
];

function EnvChip({ label, on }) {
  return (
    <div className="flex items-center justify-between gap-2 py-1.5 border-b border-sniper/60 last:border-0">
      <span className="font-mono text-[11px] uppercase text-secondary-foreground">{label}</span>
      {on ? <span className="text-olive-bright inline-flex items-center gap-1 font-mono text-[10px] uppercase"><CheckCircle2 className="w-3.5 h-3.5" /> on</span>
        : <span className="text-muted-foreground inline-flex items-center gap-1 font-mono text-[10px] uppercase"><XCircle className="w-3.5 h-3.5" /> off</span>}
    </div>
  );
}

const EMPTY_WAVE = { name: "", seat_cap: 50, released: true, notes: "" };

function WaveDialog({ initial, onClose, onSaved }) {
  const [f, setF] = useState({ ...EMPTY_WAVE, ...(initial || {}) });
  const [busy, setBusy] = useState(false);
  const editing = !!initial?.wave_id;
  const save = async () => {
    setBusy(true);
    try {
      const payload = { name: f.name, seat_cap: Number(f.seat_cap), released: !!f.released, notes: f.notes || null };
      if (editing) await api.put(`/admin/beta/waves/${initial.wave_id}`, payload);
      else await api.post("/admin/beta/waves", payload);
      toast.success(editing ? "Wave updated." : "Wave created.");
      onSaved();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const inputCls = "w-full bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-surface border-sniper text-foreground max-w-md" data-testid="wave-dialog">
        <DialogHeader><DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2"><Layers className="w-5 h-5 text-olive-bright" /> {editing ? "Edit" : "New"} Beta Wave</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><label className="font-mono text-[11px] uppercase text-secondary-foreground">Name</label>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="wave-name" className={inputCls} placeholder="Wave 1 — Founders" /></div>
          <div><label className="font-mono text-[11px] uppercase text-secondary-foreground">Seat cap</label>
            <input type="number" min="0" value={f.seat_cap} onChange={(e) => setF({ ...f, seat_cap: e.target.value })} data-testid="wave-seatcap" className={inputCls} /></div>
          <label className="flex items-center gap-2 font-mono text-xs uppercase text-secondary-foreground"><input type="checkbox" checked={f.released} onChange={(e) => setF({ ...f, released: e.target.checked })} data-testid="wave-released" /> Released (seats count toward capacity)</label>
          <div><label className="font-mono text-[11px] uppercase text-secondary-foreground">Notes</label>
            <input value={f.notes || ""} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="wave-notes" className={inputCls} /></div>
          <button onClick={save} disabled={busy || !f.name} data-testid="wave-save" className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase py-2.5 transition-colors">{busy ? "Saving…" : editing ? "Save wave" : "Create wave"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BetaWaves() {
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(null);
  const load = () => api.get("/admin/beta/waves").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const remove = async (w) => { if (!window.confirm(`Delete "${w.name}"?`)) return; try { await api.delete(`/admin/beta/waves/${w.wave_id}`); toast.success("Deleted."); load(); } catch (err) { toast.error(formatError(err.response?.data?.detail)); } };
  if (!data) return null;
  const pct = data.seats_total > 0 ? Math.min(100, (data.seats_used / data.seats_total) * 100) : 0;
  return (
    <div className="mt-8 border border-sniper bg-surface p-5" data-testid="beta-waves">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2"><Layers className="w-4 h-4 text-olive-bright" /> Beta Access — Waves & Seat Cap</h3>
        <button onClick={() => setDialog({})} data-testid="wave-new-btn" className="inline-flex items-center gap-1.5 border border-olive-bright/50 text-olive-bright hover:bg-olive/10 font-mono uppercase text-xs px-3 py-2 transition-colors"><Plus className="w-3.5 h-3.5" /> New Wave</button>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <div className="flex-1 h-3 bg-obsidian border border-sniper"><div className={`h-full ${data.full ? "bg-blaze" : "bg-olive"}`} style={{ width: `${pct}%` }} /></div>
        <span className="font-mono text-sm text-foreground" data-testid="beta-seat-status">{data.seats_used} / {data.seats_total} seats {data.full ? "· FULL" : ""}</span>
      </div>
      {data.using_env_fallback && <p className="text-[11px] font-mono uppercase text-muted-foreground mt-2">No waves configured — using env fallback cap of {data.env_fallback_cap}. Create a wave to take control.</p>}
      <div className="mt-4 divide-y divide-sniper border border-sniper" data-testid="waves-list">
        {data.waves.length === 0 && <p className="p-4 font-mono text-xs text-muted-foreground uppercase">No waves yet.</p>}
        {data.waves.map((w) => (
          <div key={w.wave_id} className="flex items-center justify-between gap-3 p-3" data-testid={`wave-row-${w.wave_id}`}>
            <div className="min-w-0">
              <div className="font-body text-sm text-foreground flex items-center gap-2">{w.name}
                {w.released ? <span className="text-[9px] uppercase text-olive-bright border border-olive-bright/50 px-1">released</span> : <span className="text-[9px] uppercase text-muted-foreground border border-sniper px-1">held</span>}</div>
              <div className="font-mono text-[11px] text-muted-foreground">{w.seats_filled} / {w.seat_cap} filled{w.notes ? ` · ${w.notes}` : ""}</div>
            </div>
            <div className="shrink-0">
              <button onClick={() => setDialog(w)} data-testid={`wave-edit-${w.wave_id}`} className="p-1.5 text-muted-foreground hover:text-foreground"><Pencil className="w-3.5 h-3.5" /></button>
              <button onClick={() => remove(w)} data-testid={`wave-delete-${w.wave_id}`} className="p-1.5 text-muted-foreground hover:text-blaze"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
      </div>
      {dialog && <WaveDialog initial={dialog.wave_id ? dialog : null} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
    </div>
  );
}

export default function AdminSettings() {
  // Load can FAIL: 10s timeout + retry + abort-on-unmount (no more silent "Loading…" forever).
  const { status, data: loaded, error, retry } = useAsyncLoad(
    (signal) => api.get("/admin/platform/settings", { signal }).then((r) => r.data), []);
  const [editable, setEditable] = useState(null);
  const [env, setEnv] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (loaded) { setEditable(loaded.editable); setEnv(loaded.environment); } }, [loaded]);

  const save = async () => {
    setBusy(true);
    try {
      const flat = Object.fromEntries(FIELDS.map((f) => [f.key, Math.max(0, parseInt(editable[f.key] || 0, 10))]));
      const bt = Object.fromEntries(BADGE_FIELDS.map((f) => [f.key, Math.max(0, Number((editable.badge_thresholds || {})[f.key] ?? 0))]));
      const payload = { ...flat, badge_thresholds: bt };
      const { data } = await api.put("/admin/platform/settings", payload);
      setEditable(data.editable);
      toast.success("Platform settings saved.");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setBadge = (k, v) => setEditable((e) => ({ ...e, badge_thresholds: { ...(e.badge_thresholds || {}), [k]: v } }));

  if (!editable) {
    if (status === "error") return <LoadFailed reason={`Couldn't load platform settings — ${error}`} onRetry={retry} testid="admin-settings-load-failed" />;
    return <Loading label="Loading platform settings…" testid="admin-settings-loading" />;
  }

  return (
    <div className="max-w-[1000px] mx-auto px-4 py-6 text-foreground" data-testid="admin-settings">
      <h2 className="font-heading text-4xl font-bold uppercase tracking-tight flex items-center gap-2"><Settings className="w-7 h-7 text-olive-bright" /> Settings</h2>
      <p className="text-sm font-body text-secondary-foreground mt-2">Platform configuration, privacy retention windows, integration status, and beta access.</p>
      <Crosshair className="my-5" />

      <div className="border border-sniper bg-surface p-5" data-testid="settings-form">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground flex items-center gap-2"><Shield className="w-4 h-4 text-olive-bright" /> Platform Configuration</h3>
        <div className="mt-4 space-y-3">
          {FIELDS.map((f) => (
            <div key={f.key} className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono text-sm text-secondary-foreground uppercase flex items-center gap-1.5"><f.icon className="w-3.5 h-3.5 text-muted-foreground" /> {f.label}</div>
                <div className="text-[10px] font-mono uppercase text-muted-foreground">{f.note}</div>
              </div>
              <input type="number" min="0" value={editable[f.key]} onChange={(e) => setEditable({ ...editable, [f.key]: e.target.value })}
                data-testid={`setting-${f.key}`} className="w-28 shrink-0 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm text-right" />
            </div>
          ))}
        </div>

        <div className="mt-6 pt-5 border-t border-sniper" data-testid="badge-thresholds">
          <h4 className="font-heading text-base uppercase tracking-tight text-foreground flex items-center gap-2"><Award className="w-4 h-4 text-olive-bright" /> Earned-Badge Thresholds</h4>
          <p className="text-[10px] font-mono uppercase text-muted-foreground mt-1">Tune Rating / Top Rated / Fast Shipper / Quick Reply. Re-grades everyone on the next hourly pass — no redeploy.</p>
          <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {BADGE_FIELDS.map((f) => (
              <div key={f.key} className="flex items-center justify-between gap-3">
                <span className="font-mono text-[11px] text-secondary-foreground uppercase">{f.label}</span>
                <input type="number" min="0" step="1" value={(editable.badge_thresholds || {})[f.key] ?? ""}
                  onChange={(e) => setBadge(f.key, e.target.value)}
                  data-testid={`badge-threshold-${f.key}`}
                  className="w-24 shrink-0 bg-input border border-sniper focus:border-olive outline-none px-3 py-2 text-foreground font-mono text-sm text-right" />
              </div>
            ))}
          </div>
        </div>

        <button onClick={save} disabled={busy} data-testid="settings-save" className="mt-5 inline-flex items-center gap-2 bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase px-6 py-2.5 transition-colors"><Save className="w-4 h-4" /> {busy ? "Saving…" : "Save settings"}</button>
      </div>

      <div className="mt-4 border border-sniper bg-surface p-5" data-testid="settings-env">
        <h3 className="font-heading text-lg uppercase tracking-tight text-foreground">Environment & Integrations</h3>
        <p className="text-[11px] font-mono uppercase text-muted-foreground mt-1">Read-only — set in deployment secrets.</p>
        <div className="mt-3 grid sm:grid-cols-2 gap-x-8">
          <EnvChip label="Beta splash gate" on={env.beta_splash_enabled} />
          <EnvChip label="Resend (email)" on={env.resend_configured} />
          <EnvChip label="Google OAuth" on={env.google_oauth_configured} />
          <EnvChip label="VPN/proxy detection" on={env.vpn_detection_enabled} />
          <EnvChip label="Cloudflare-only geo" on={env.cloudflare_only} />
          <EnvChip label="SITREP IP pepper" on={env.ip_pepper_configured} />
        </div>
        <div className="mt-3 font-mono text-[11px] uppercase text-muted-foreground">Sender: <span className="text-secondary-foreground">{env.sender_email || "—"}</span> · Stripe mode: <span className="text-secondary-foreground">{env.stripe_mode}</span></div>
      </div>

      <VerificationTaxonomyAudit />

      <CommissionImpactReport />

      <BetaWaves />
    </div>
  );
}
